import { VOICE_BASE_PATH, VOICE_MANIFEST_FILE } from '../constants/config';
import { createVoiceClipKey } from '../utils/voiceUtils';
import { isSpeechSupported, speakText, stopSpeaking } from '../utils/speechUtils';

/**
 * Reads Fizzy's messages aloud. Each segment plays its pre-recorded ElevenLabs clip when
 * there is one (listed in the voice manifest); otherwise the browser's own voice reads it.
 */
export default class VoicePlayer {
  constructor(basePath = VOICE_BASE_PATH) {
    this.basePath = basePath;
    this.recordedKeys = new Set();
    this.currentAudio = null;
    this.playbackId = 0;
    this.manifestPromise = null;
  }

  /**
   * True when this browser can play recordings or speak with its own voice.
   */
  static isAvailable() {
    return isSpeechSupported() || typeof Audio !== 'undefined';
  }

  loadManifest() {
    this.manifestPromise ||= fetch(`${this.basePath}/${VOICE_MANIFEST_FILE}`)
      .then((response) => (response.ok ? response.json() : { clips: {} }))
      .then((manifest) => {
        this.recordedKeys = new Set(Object.keys(manifest.clips ?? {}));
      })
      .catch(() => {
        // No recordings yet: every segment uses the browser voice.
      });
    return this.manifestPromise;
  }

  hasRecording(text) {
    return this.recordedKeys.has(createVoiceClipKey(text));
  }

  /**
   * Speaks the segments one after another. onEnd runs once when all are done or playback is stopped.
   */
  async speak(segments, { onEnd } = {}) {
    this.stop();
    const playbackId = ++this.playbackId;
    const isCurrentPlayback = () => playbackId === this.playbackId;
    if (typeof fetch === 'function') await this.loadManifest();

    for (const segment of segments) {
      if (!isCurrentPlayback()) return;
      await (this.hasRecording(segment) ? this.playRecording(segment) : this.speakWithBrowserVoice(segment));
    }
    if (isCurrentPlayback()) onEnd?.();
  }

  stop() {
    this.playbackId += 1;
    if (this.currentAudio) {
      this.currentAudio.pause();
      this.currentAudio = null;
    }
    stopSpeaking();
  }

  playRecording(text) {
    return new Promise((resolve) => {
      const audio = new Audio(`${this.basePath}/${createVoiceClipKey(text)}.mp3`);
      this.currentAudio = audio;
      const finish = () => {
        if (this.currentAudio === audio) this.currentAudio = null;
        resolve();
      };
      const fallBackToBrowserVoice = () => this.speakWithBrowserVoice(text).then(finish);
      audio.addEventListener('ended', finish, { once: true });
      audio.addEventListener('pause', finish, { once: true });
      audio.addEventListener('error', fallBackToBrowserVoice, { once: true });
      audio.play()?.catch(fallBackToBrowserVoice);
    });
  }

  speakWithBrowserVoice(text) {
    return new Promise((resolve) => speakText(text, { onEnd: resolve }));
  }
}
